import axios from 'axios';
import { getAuthHeaders } from '../utils/authUtils';
import { LinkTypes, ReturnCodes } from '../utils/constants';
import fileSystemAccessService from './FileSystemAccessService';

class VideoCRUDService {
    createPostRequest(
        link_Type,
        isCompressed,
        selectOpenAI,
        isLogging,
        skipTranslationIsChecked,
        onlyTransliteration,
        bitRate,
        crfValue,
        folderPath = null,
    ) {
        // Base URL depends on video experience
        let url = '';
        if (folderPath) {
            url = `${process.env.REACT_APP_API_URL}/api/v3/videos/createInFolder`;
        } else {
            url = `${process.env.REACT_APP_API_URL}/api/v3/videos`;
        }

        // Add query parameters
        let options = url;

        if (isCompressed) options += '?compressed=true';
        else options += '?compressed=false';

        if (folderPath) {
            options += '&folder=' + folderPath;
        }

        if (selectOpenAI) options += '&modelSelection=openai';
        else options += '&modelSelection=custom';

        if (isLogging) options += '&detailedLogging=true';
        else options += '&detailedLogging=false';

        if (skipTranslationIsChecked) options += '&skipTranslation=true';
        else options += '&skipTranslation=false';

        if (onlyTransliteration) options += '&onlyTransliteration=true';
        else options += '&onlyTransliteration=false';

        if (bitRate) options += '&bitRate=' + bitRate;
        if (crfValue) options += '&crf=' + crfValue;

        if (link_Type === LinkTypes.YOUTUBE) {
            options += '&link=youtube';
        } else if (link_Type === LinkTypes.LOCAL_FILE) {
            options += '&link=local';
        } else {
            options += '&link=gdrive';
        }

        const selectedVideoLanguages = JSON.parse(localStorage.getItem('createVideoLanguages'));
        console.log('selectedVideoLanguages:', selectedVideoLanguages);
        if (selectedVideoLanguages !== null) {
            // selectedVideoLanguages is in the format of "[{value: 'eng', label: 'English', isFixed: true}]"
            // just read the value of each item and join them with a comma
            options += '&createLanguages=' + selectedVideoLanguages.join(',');
        } else {
            options += '&createLanguages=eng';
        }

        return options;
    }

    async isVideoURLValid(url, youTubeURL, linkType) {
        if (linkType === LinkTypes.YOUTUBE) {
            const youTubeVideoName = youTubeURL.split('v=')[1];
            const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY;
            url = `https://www.googleapis.com/youtube/v3/videos?id=${youTubeVideoName}&key=${apiKey}&part=snippet,contentDetails,statistics,status`;
        }

        const response = await axios.get(url);
        if (response.status === 200) {
            return true;
        } else {
            return false;
        }
    }

    async addVideoInFolderWithPromise(
        link_Type,
        url,
        userData,
        videoName,
        folderPath,
        isCompressed,
        selectOpenAI,
        isLogging,
        skipTranslationIsChecked,
        onlyTransliteration,
        bitRate,
        crfValue,
    ) {
        return new Promise(async (resolve, reject) => {
            try {
                let apiEndpoint;

                if (folderPath) {
                    // For videos in folders, use appropriate API based on experience
                    apiEndpoint = this.createPostRequest(
                        link_Type,
                        isCompressed,
                        selectOpenAI,
                        isLogging,
                        skipTranslationIsChecked,
                        onlyTransliteration,
                        bitRate,
                        crfValue,
                        folderPath,
                    );
                } else {
                    // For root folder or simple video add
                    apiEndpoint = this.createPostRequest(
                        link_Type,
                        isCompressed,
                        selectOpenAI,
                        isLogging,
                        skipTranslationIsChecked,
                        onlyTransliteration,
                        bitRate,
                        crfValue,
                    );
                }

                await axios
                    .post(
                        apiEndpoint,
                        {
                            videoUrl: url,
                            videoName: videoName,
                        },
                        {
                            headers: {
                                'Access-Control-Allow-Origin': '*',
                                'Access-Control-Allow-Credentials': true,
                                email: userData.email,
                                auth: userData.access,
                            },
                        },
                    )
                    .then(function (response) {
                        // If using MongoDB API with folderPath, the video is already added to the folder
                        if (folderPath && folderPath !== '/') {
                            resolve({
                                status: ReturnCodes.FILE_UPLOAD_SUCCESS,
                                id: response.data.id,
                                videoName: response.data.videoName,
                                ...response.data,
                            });
                        } else {
                            // Old flow - call addVideo2Folder if needed
                            if (folderPath && folderPath !== '/') {
                                fileSystemAccessService
                                    .addVideo2Folder(folderPath, response.data.videoUrl, response.data.id, userData)
                                    .then(function (folderResponse) {
                                        resolve({
                                            status: ReturnCodes.FILE_UPLOAD_SUCCESS,
                                            id: response.data.id,
                                            videoName: response.data.videoName,
                                            ...response.data,
                                        });
                                    })
                                    .catch(function (error) {
                                        console.error('Error adding video to folder:', error);
                                        reject({ status: ReturnCodes.FILE_UPLOAD_FAILED });
                                    });
                            } else {
                                resolve({
                                    status: ReturnCodes.FILE_UPLOAD_SUCCESS,
                                    id: response.data.id,
                                    videoName: response.data.videoName,
                                    ...response.data,
                                });
                            }
                        }
                    })
                    .catch(function (error) {
                        console.error('Error uploading video:', error);
                        reject({ status: ReturnCodes.FILE_UPLOAD_FAILED });
                    });
            } catch (error) {
                console.error('Exception in addVideoInFolderWithPromise:', error);
                reject({ status: error });
            }
        });
    }

    /**
     * Delete a video by ID, supporting both SQL and MongoDB backends
     * This will also delete all associated exports
     *
     * @param {string} videoid - The ID of the video to delete
     * @param {string} apisurls - The folder path where the video is located
     * @param {string} email - User's email
     * @param {string} access - User's access token
     * @returns {Promise} - Promise resolving to the API response
     */
    async delete(videoid, apisurls, email, access) {
        return new Promise(async (resolve, reject) => {
            try {
                const headers = getAuthHeaders(true);
                const apiBaseUrl = process.env.REACT_APP_API_URL;
                // The backend endpoint should handle deleting exports
                const apiEndpoint = `${apiBaseUrl}/api/v3/videos/${videoid}?folder=${encodeURIComponent(apisurls)}`;

                const response = await axios.delete(apiEndpoint, { headers });

                resolve(response.data);
            } catch (error) {
                console.error('Error deleting video:', error);
                reject(error);
            }
        });
    }

    async getVideoDataOfIds(videoIdList, email, access) {
        const apiUrl = `${process.env.REACT_APP_API_URL}/api/v3/videos/all/${videoIdList}`;

        try {
            const headers = getAuthHeaders(true);
            const response = await axios.get(apiUrl, { headers });
            return response;
        } catch (error) {
            console.error('Error fetching videos by IDs:', error);
            throw error;
        }
    }

    /**
     * Rename a video by ID, supporting both SQL and MongoDB backends
     *
     * @param {string|number} videoId - The ID of the video to rename
     * @param {string} email - User's email
     * @param {string} access - User's access token
     * @param {string} newName - New name for the video
     * @returns {Promise} - Promise resolving to the API response
     */
    async rename(videoId, currentPath = null, email, access, newName) {
        return new Promise(async (resolve, reject) => {
            try {
                const headers = getAuthHeaders(true);
                // Determine the correct API endpoint based on database type
                const apiBaseUrl = process.env.REACT_APP_API_URL;
                const apiEndpoint = `${apiBaseUrl}/api/v3/videos/rename?id=${encodeURIComponent(
                    videoId,
                )}&name=${encodeURIComponent(newName)}&currentPath=${encodeURIComponent(currentPath)}`;

                const response = await axios.put(
                    apiEndpoint,
                    {}, // Empty body
                    { headers },
                );

                resolve(response);
            } catch (error) {
                console.error('Error renaming video:', error);
                reject(error);
            }
        });
    }

    /**
     * Regenerate transcriptions for a video, supporting both SQL and MongoDB backends
     *
     * @param {string|number} videoId - The ID of the video to regenerate
     * @param {string} email - User's email
     * @param {string} access - User's access token
     * @returns {Promise} - Promise resolving to the API response
     */
    async regenerate(videoId) {
        try {
            const headers = getAuthHeaders(true);
            // Determine the correct API endpoint based on database type
            const apiBaseUrl = process.env.REACT_APP_API_URL;
            const apiEndpoint = `${apiBaseUrl}/api/v3/videos/regenerate?id=${videoId}`;

            const response = await axios.put(
                apiEndpoint,
                {}, // Empty body
                { headers },
            );
            return response;
        } catch (error) {
            console.error('Error regenerating video transcription:', error);
            throw error;
        }
    }

    /**
     * Re-compress an existing video with -movflags +faststart for improved
     * seeking and streaming performance.
     *
     * @param {string} videoId - The ID of the video to re-compress
     * @returns {Promise} - Promise resolving to the API response
     */
    async recompress(videoId) {
        try {
            const headers = getAuthHeaders(true);
            const apiBaseUrl = process.env.REACT_APP_API_URL;
            const apiEndpoint = `${apiBaseUrl}/api/v3/videos/recompress?id=${videoId}`;
            const response = await axios.put(apiEndpoint, {}, { headers });
            return response;
        } catch (error) {
            console.error('Error recompressing video:', error);
            throw error;
        }
    }

    async updateProgressStage(videoId, email, access, processStage, languageName) {
        try {
            const headers = getAuthHeaders(true);
            const response = await axios.put(
                process.env.REACT_APP_API_URL +
                    `/api/v2/videos/processStage?id=${videoId}&newProcessStage=${processStage}&languageName=${languageName}`,
                {},
                {
                    headers,
                },
            );
            return response;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update the process stage for a specific language in a video
     *
     * @param {string|number} videoId - The ID of the video
     * @param {string} languageName - The language name to update
     * @param {string} newProcessStage - The new process stage value
     * @param {string} email - User's email
     * @param {string} access - User's access token
     * @param {boolean} useMongoDb - Whether to use MongoDB API
     * @returns {Promise} - Promise resolving to the API response
     */
    async updateProcessStage(videoId, languageName, newProcessStage, email, access) {
        try {
            // Determine the correct API endpoint based on database type
            const apiBaseUrl = process.env.REACT_APP_API_URL;
            const apiEndpoint = `${apiBaseUrl}/api/v3/videos/processStage`;
            const headers = getAuthHeaders(true);
            // Build query parameters
            const params = new URLSearchParams();
            params.append('id', videoId);
            params.append('languageName', languageName);
            params.append('newProcessStage', newProcessStage);

            const response = await axios.put(
                `${apiEndpoint}?${params.toString()}`,
                {}, // Empty body
                {
                    headers,
                },
            );
            return response;
        } catch (error) {
            console.error('Error updating process stage:', error);
            throw error;
        }
    }

    /**
     * Initiate text-to-text translation for a video, supporting both SQL and MongoDB backends
     *
     * @param {string|number} videoId - The ID of the video
     * @param {string} sourceLanguage - The source language to translate from
     * @param {string} targetLanguages - Comma-separated list of languages to translate to
     * @param {boolean} skipTranslation - Whether to skip translation and create empty files
     * @param {string} email - User's email
     * @param {string} access - User's access token
     * @returns {Promise} - Promise resolving to the API response
     */
    async text2Text(videoId, sourceLanguage, targetLanguages, skipTranslation, email, access) {
        try {
            const headers = getAuthHeaders(true);
            // Determine the correct API endpoint based on database type
            const apiBaseUrl = process.env.REACT_APP_API_URL;
            const apiEndpoint = `${apiBaseUrl}/api/v3/videos/text2text`;

            // Build query parameters
            const params = new URLSearchParams();
            params.append('videoId', videoId);
            params.append('sourceLanguage', sourceLanguage);
            params.append('targetLanguages', targetLanguages);
            params.append('skipTranslation', skipTranslation);

            const response = await axios.post(
                `${apiEndpoint}?${params.toString()}`,
                {}, // Empty body
                {
                    headers,
                },
            );
            return response;
        } catch (error) {
            console.error('Error in text-to-text translation:', error);
            throw error;
        }
    }

    async getVideos() {
        const headers = getAuthHeaders(true);

        const apiUrl = `${process.env.REACT_APP_API_URL}/api/v3/videos`;

        try {
            const response = await axios.get(apiUrl, {
                headers,
            });
            return response;
        } catch (error) {
            throw error;
        }
    }

    async getVideoById(videoId, email, access) {
        const headers = getAuthHeaders(true);
        const apiUrl = `${process.env.REACT_APP_API_URL}/api/v3/videos/${videoId}`;

        try {
            const response = await axios.get(apiUrl, { headers });
            return response;
        } catch (error) {
            console.error('Error fetching video details:', error);
            throw error;
        }
    }

    async getVideoDataOfId(videoId, email, access) {
        return this.getVideoById(videoId, email, access);
    }

    /**
     * Save metadata for a video by ID, supporting both SQL and MongoDB backends
     *
     * @param {string|number} videoId - The ID of the video
     * @param {boolean} bookmarked - Whether the video is bookmarked
     * @param {string} subtitleComment - Comment for the subtitle
     * @param {string} languageName - The language name
     * @param {string} email - User's email
     * @param {string} access - User's access token
     * @returns {Promise} - Promise resolving to the API response
     */
    async saveMetaDataforVideoId(
        videoId,
        bookmarked,
        subtitleComment,
        languageName,
        saveType = 'manual',
        importSource = null,
    ) {
        console.log('🌐 API call starting:', {
            videoId,
            languageName,
            saveType,
            bookmarkedCount: bookmarked?.length || 0,
            commentCount: subtitleComment?.length || 0,
            timestamp: new Date().toISOString(),
        });

        try {
            const headers = getAuthHeaders(true);
            const apiBaseUrl = process.env.REACT_APP_API_URL;
            const apiEndpoint = `${apiBaseUrl}/api/v3/videos/${videoId}`;

            console.log('📡 Making PUT request to:', apiEndpoint);

            const response = await axios.put(
                apiEndpoint,
                {
                    languageName: languageName,
                    metadataCheckFlag: bookmarked,
                    metadataComments: subtitleComment,
                    saveType: saveType,
                    importSource: importSource,
                },
                {
                    headers,
                },
            );

            console.log('✅ API response received:', {
                status: response.status,
                language: languageName,
                dataExists: !!response.data,
            });

            return response;
        } catch (error) {
            console.error('💥 API call failed:', {
                videoId,
                languageName,
                error: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
            });
            throw error;
        }
    }

    /**
     * Save the video editor state (cuts, cutMarkers, music, logo) to the backend.
     *
     * @param {string} videoId - The video ID
     * @param {Object} editorState - { cutMarkers, cuts, music, logo, savedAt, savedBy }
     * @returns {Promise} - Promise resolving to the API response
     */
    async saveVideoEditorState(videoId, editorState) {
        try {
            const headers = getAuthHeaders(true);
            const apiBaseUrl = process.env.REACT_APP_API_URL;
            const apiEndpoint = `${apiBaseUrl}/api/v3/videos/${videoId}/editor-state`;
            const response = await axios.put(apiEndpoint, editorState, { headers });
            return response;
        } catch (error) {
            console.error('💥 saveVideoEditorState failed:', {
                videoId,
                error: error.message,
                status: error.response?.status,
            });
            throw error;
        }
    }

    /**
     * Delete multiple videos by ID, supporting both SQL and MongoDB backends
     * This will also delete all associated exports for each video
     *
     * @param {string|array} videos - Single ID or array of video IDs to delete
     * @param {string} folder - The folder path where the videos are located
     * @param {string} email - User's email
     * @param {string} access - User's access token
     * @returns {Promise} - Promise resolving to the API response
     */
    async deleteVideos(videos, folder, email, access) {
        try {
            const headers = getAuthHeaders(true);
            const apiBaseUrl = process.env.REACT_APP_API_URL;
            
            // Ensure we're sending array of video IDs
            const videoIds = Array.isArray(videos) ? videos : [videos];
            
            const apiEndpoint = `${apiBaseUrl}/api/v3/videos/batch?folder=${encodeURIComponent(folder)}`;

            const response = await axios.delete(apiEndpoint, {
                headers,
                data: { videoIds } // Backend should handle deleting exports for all videos
        });

        return response.data;
    } catch (error) {
        console.error('Error deleting multiple videos:', error);
        throw error;
    }
}

/**
 * Move a video file to a different folder
 *
 * @param {string} videoId - The ID of the video to move
 * @param {string} sourcePath - Current path of the video
 * @param {string} destinationPath - Path to move the video to
 * @param {string} email - User's email
 * @param {string} access - User's access token
 * @returns {Promise} - Promise resolving to the API response
 */
async moveFile(videoId, sourcePath, destinationPath, email, access) {
    try {
        const apiBaseUrl = process.env.REACT_APP_API_URL;
        const headers = getAuthHeaders(true);
        const apiEndpoint = `${apiBaseUrl}/api/v3/videos/${videoId}/move`;

        const response = await axios.post(
            apiEndpoint,
            {
                sourcePath,
                destinationPath,
            },
            {
                headers,
            },
        );

        return response.data;
    } catch (error) {
        console.error('Error moving video file:', error);
        throw error;
    }
}

/**
 * Saves subtitle content and related metadata for a video
 *
 * @param {Array} subtitle - The subtitle data to save
 * @param {string} selectedLng - The language being edited
 * @param {boolean} bookmarked - Whether the video is bookmarked
 * @param {string} subtitleComment - Comments on the subtitle
 * @param {Function} addLog - Function to log actions
 * @param {boolean} showFeedback - Whether to show user feedback/alerts
 * @returns {Promise<Object>} Result object with success status and response data
 */
async saveSubtitleContent(
    subtitle,
    selectedLng,
    bookmarked,
    subtitleComment,
    addLog = () => {},
    showFeedback = true,
    importSource = null,
) {
    console.log('🎯 VideoCRUDService.saveSubtitleContent called with:', {
        subtitleCount: subtitle?.length || 0,
        selectedLanguage: selectedLng,
        showFeedback,
        importSource,
        bookmarkCount: bookmarked?.length || 0,
        commentCount: subtitleComment?.length || 0,
    });

    try {
        // Parse video properties
        const videoProps = JSON.parse(localStorage.getItem('videoProps'));
        console.log('📹 Video ID:', videoProps?.id, 'Video Name:', videoProps?.videoName);

        // Don't attempt to save if no subtitles exist
        if (!subtitle || subtitle.length === 0) {
            console.log('❌ No subtitles to save');
            return { success: false, message: 'No subtitles to save' };
        }

        // Find the language index in videoProps
        let languageIndex = -1;
        for (let i = 0; i < videoProps.videoLanguages.length; i++) {
            if (videoProps.videoLanguages[i].languageName === selectedLng) {
                languageIndex = i;
                break;
            }
        }

        console.log('🔍 Language lookup:', {
            selectedLanguage: selectedLng,
            languageIndex,
            availableLanguages: videoProps.videoLanguages.map((l) => l.languageName),
        });

        if (languageIndex === -1) {
            const message = `Language '${selectedLng}' not found in video properties`;
            console.error('❌', message);
            return { success: false, message };
        }

        const vttFileURL = videoProps.videoLanguages[languageIndex].vttFileUrl;
        console.log('📂 VTT File URL:', vttFileURL);

        // Check user permissions
        if (!this.areSubtitlesEditable(selectedLng)) {
            const authUser = JSON.parse(localStorage.getItem('authUser'));
            const message = `User ${
                authUser?.email || 'Unknown'
            } does not have permission to edit ${selectedLng} subtitles`;
            console.error('🚫 Permission denied:', message);
            return { success: false, message };
        }

        console.log('✅ User has permission to edit', selectedLng);

        // Create parsed subtitle object
        const parsedSubtitle = { cues: [], valid: true };

        // Convert subtitle format for VTT
        subtitle.forEach((subtitleBlock, index) => {
            const startSeconds = this.convertTimeToSeconds(subtitleBlock.start);
            const endSeconds = this.convertTimeToSeconds(subtitleBlock.end);

            const cue = {
                identifier: '',
                start: startSeconds,
                end: endSeconds,
                text: subtitleBlock.text2 || subtitleBlock.text || '',
                settings: '',
            };

            // Add additional properties if they exist
            if (subtitleBlock.Estart) cue.Estart = this.convertTimeToSeconds(subtitleBlock.Estart);
            if (subtitleBlock.Eend) cue.Eend = this.convertTimeToSeconds(subtitleBlock.Eend);
            if (subtitleBlock.Etext) cue.Etext = subtitleBlock.Etext;
            if (subtitleBlock.note) cue.note = subtitleBlock.note;
            if (subtitleBlock.bookmark) cue.bookmark = subtitleBlock.bookmark;

            parsedSubtitle.cues.push(cue);
        });

        console.log('📝 Parsed subtitle cues:', parsedSubtitle.cues.length);

        // Filter out empty cues
        parsedSubtitle.cues = parsedSubtitle.cues.filter((cue) => {
            return !isNaN(cue.start) && !isNaN(cue.end) && (cue.start > 0 || cue.end > 0 || cue.text !== '');
        });

        console.log('🧹 Filtered cues count:', parsedSubtitle.cues.length);

        if (parsedSubtitle.cues.length === 0) {
            const message = 'No valid subtitles to save after filtering';
            console.error('❌', message);
            return { success: false, message };
        }

        // Compile the subtitle content to VTT format
        console.log('🔧 Starting VTT compilation...');
        const { compile } = await import('../libs/webvttnotes/compiler');
        const modifiedSubtitleContent = compile(parsedSubtitle, { strict: false });
        console.log('✅ VTT compilation completed, content length:', modifiedSubtitleContent.length);

        // Upload to blob storage
        console.log('☁️ Uploading to blob storage...');
        addLog(
            `Attempting to save to Blob Storage: Video = ${videoProps.id}, VideoName = ${videoProps.videoName}, Language = ${selectedLng}`,
        );

        const { uploadToBlobStorage } = await import('../folders/subtitles/BlobVttFile');
        await uploadToBlobStorage(vttFileURL, modifiedSubtitleContent, 'vtt');

        console.log('✅ Blob storage upload completed for', selectedLng);
        addLog(
            `Successfully saved to Blob Storage: Video = ${videoProps.id}, VideoName = ${videoProps.videoName}, Language = ${selectedLng}`,
        );

        // Save metadata
        console.log('💾 Saving metadata...');
        const authUser = JSON.parse(localStorage.getItem('authUser'));
        const authToken = localStorage.getItem('authToken');

        const response = await this.saveMetaDataforVideoId(
            videoProps.id,
            bookmarked,
            subtitleComment,
            selectedLng,
            showFeedback ? 'manual' : importSource == null ? 'auto' : 'import',
            importSource,
        );

        console.log('✅ Metadata saved successfully for', selectedLng);
        addLog(
            `${showFeedback ? '' : 'Auto '}Saved metadata for Video = ${videoProps.id}, VideoName = ${
                videoProps.videoName
            }, Language = ${selectedLng}`,
        );

        // Update localStorage with the updated video properties
        if (response && response.data) {
            localStorage.setItem('videoProps', JSON.stringify(response.data));
            console.log('💾 Updated videoProps in localStorage');
        }

        // Show success feedback if requested
        if (showFeedback && window.Swal) {
            window.Swal.fire({
                title: 'Success!',
                text: 'Your changes have been saved!',
                icon: 'success',
                confirmButtonText: 'Close',
            });
        }

        console.log('🎉 Complete save success for', selectedLng);
        return { success: true, data: response?.data };
    } catch (error) {
        console.error('💥 Error in saveSubtitleContent for', selectedLng, ':', error);
        console.error('Error stack:', error.stack);
        return { success: false, error };
    }
}

/**
 * Converts time string (HH:MM:SS.mmm) to seconds (as a number)
 *
 * @param {string} timeString - Time in format "HH:MM:SS.mmm" or "MM:SS.mmm"
 * @returns {number} - Time in seconds
 */
convertTimeToSeconds(timeString) {
    if (!timeString) return 0;

    // Handle various time formats
    const parts = timeString.split(':');
    let hours = 0,
        minutes = 0,
        seconds = 0;

    if (parts.length === 3) {
        // HH:MM:SS format
        hours = parseInt(parts[0], 10) || 0;
        minutes = parseInt(parts[1], 10) || 0;
        seconds = parseFloat(parts[2]) || 0;
    } else if (parts.length === 2) {
        // MM:SS format
        minutes = parseInt(parts[0], 10) || 0;
        seconds = parseFloat(parts[1]) || 0;
    } else if (parts.length === 1) {
        // SS format
        seconds = parseFloat(parts[0]) || 0;
    }

    // Calculate total seconds
    return hours * 3600 + minutes * 60 + seconds;
}

/**
 * Helper method to check if subtitles are editable for a language based on user permissions
 *
 * @param {string} language - The language to check
 * @returns {boolean} - Whether the language is editable for the current user
 */
areSubtitlesEditable(language) {
    try {
        // Get the current authenticated user
        const authUser = JSON.parse(localStorage.getItem('authUser'));
        if (!authUser) return false;

        // Check if user has language-specific permissions in their profile
        if (authUser.languageAccess && typeof authUser.languageAccess === 'object') {
            // For admin users, check if they have explicit access to this language
            // This allows for more fine-grained control even for admins
            if (authUser.userType === 'admin') {
                // If language is not specified in languageAccess, admins can edit it
                if (!authUser.languageAccess[language]) return true;

                // Otherwise, check their specific access level
                return (
                    authUser.languageAccess[language] === 'WRITE' || authUser.languageAccess[language] === 'ADMIN'
                );
            }

            // For non-admin users, check their specific language access
            const accessLevel = authUser.languageAccess[language];
            return accessLevel === 'WRITE' || accessLevel === 'ADMIN';
        }

        // Fallback for users without languageAccess property
        // Admin users can edit all languages by default
        if (authUser.userType === 'admin') return true;

        // For backward compatibility - use the old hardcoded list
        const editableLanguages = ['English', 'Hindi', 'Gujarati'];
        return editableLanguages.includes(language);
    } catch (error) {
        console.error('Error checking subtitle edit permissions:', error);
        return false; // Default to no permission on error
    }
}

// Clear version history for a video
clearVersionHistory(videoId) {
    const apiBaseUrl = process.env.REACT_APP_API_URL;

    const apiEndpoint = `${apiBaseUrl}/api/v3/videos/${videoId}/languages`;

    return axios.delete(apiEndpoint);
}

/**
 * Gets the current processing status of a video
 *
 * @param {string} videoId - The ID of the video
 * @param {string} email - User email
 * @param {string} auth - Authentication token
 * @returns {Promise} - Promise resolving to the processing status
 */
async getVideoProcessingStatus(videoId, email, auth) {
    try {
        const headers = getAuthHeaders(true);
        const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/v3/videos/${videoId}/processing-status`,
            {
                headers,
            },
        );
        return response;
    } catch (error) {
        console.error('Error getting video processing status:', error);
        throw error;
    }
}

async terminateTranslation(videoId, languageName) {
    try {
        const url = `${process.env.REACT_APP_API_URL}/api/v3/video/${videoId}/language/${languageName}/terminate`;
        const response = await axios.post(url, {}, { headers: getAuthHeaders(true) });
        return response.data;
    } catch (error) {
        console.error('Failed to terminate translation:', error);
        throw error;
    }
}

/**
 * Upload a video with subtitles and start text-to-text translation in one step
 *
 * @param {string} linkType - The type of link (GOOGLE_DRIVE, YOUTUBE, LOCAL_FILE)
 * @param {string} url - URL of the video
 * @param {Object} userData - User authentication data
 * @param {string} videoName - Name for the video
 * @param {string} folderPath - Path to place the video in
 * @param {File} subtitleFile - Subtitle file to upload
 * @param {string} sourceLanguage - Language of the subtitle file
 * @param {string} targetLanguages - Comma-separated list of languages to translate to
 * @param {boolean} isCompressed - Whether the video is already compressed
 * @returns {Promise} - Promise resolving to the API response
 */
async uploadVideoWithSubtitles(
    linkType,
    url,
    userData,
    videoName,
    folderPath,
    subtitleFile,
    sourceLanguage,
    targetLanguages,
    isCompressed,
) {
    return new Promise(async (resolve, reject) => {
        const headers = getAuthHeaders(true);

        try {
            // Read the subtitle file content as text
            const subtitleContent = await this.readFileAsText(subtitleFile);

            // Build the API endpoint with query parameters
            const apiBaseUrl = process.env.REACT_APP_API_URL;
            let endpoint = `${apiBaseUrl}/api/v3/videos/createWithSubtitles`;

            // Add query parameters
            const params = new URLSearchParams();
            params.append('sourceLanguage', sourceLanguage);
            if (targetLanguages) params.append('targetLanguages', targetLanguages);
            if (isCompressed) params.append('compressed', 'true');
            else params.append('compressed', 'false');
            if (folderPath) params.append('folder', folderPath);
            params.append('linkType', linkType);

            // Determine link type parameter for backend
            if (linkType === LinkTypes.YOUTUBE) {
                params.append('link', 'youtube');
            } else if (linkType === LinkTypes.LOCAL_FILE) {
                params.append('link', 'local');
            } else {
                params.append('link', 'gdrive');
            }

            const finalEndpoint = `${endpoint}?${params.toString()}`;

            // Send as JSON instead of FormData
            const requestBody = {
                videoUrl: url,
                videoName: videoName,
                subtitleContent: subtitleContent,
                subtitleFileName: subtitleFile.name,
            };

            await axios
                .post(finalEndpoint, requestBody, {
                    headers,
                })
                .then(function (response) {
                    // Success - video uploaded with subtitles
                    resolve({
                        status: ReturnCodes.FILE_UPLOAD_SUCCESS,
                        id: response.data.id,
                        videoName: response.data.videoName,
                        ...response.data,
                    });
                })
                .catch(function (error) {
                    console.error('Error uploading video with subtitles:', error);
                    reject({ status: ReturnCodes.FILE_UPLOAD_FAILED, error: error });
                });
        } catch (error) {
            console.error('Exception in uploadVideoWithSubtitles:', error);
            reject({ status: error });
        }
    });
}

/**
 * Initiate voice-over generation for a video
 *
 * @param {string|number} videoId - The ID of the video
 * @param {string} sourceLanguage - The source language to generate voice from
 * @param {string} targetLanguages - Comma-separated list of languages to generate voice for
 * @param {boolean} skipProcessing - Whether to skip processing and create empty files
 * @param {string} email - User's email
 * @param {string} access - User's access token
 * @returns {Promise} - Promise resolving to the API response
 */
async generateVoiceOver(videoId, sourceLanguage, targetLanguages, skipAI = false) {
    const headers = getAuthHeaders(true);

    return new Promise(async (resolve, reject) => {
        try {
            const apiBaseUrl = process.env.REACT_APP_API_URL;
            const endpoint = `${apiBaseUrl}/api/v3/videos/${videoId}/voiceover`;

            const response = await axios.post(
                endpoint,
                {
                    sourceLanguage,
                    targetLanguages,
                    skipAI: String(skipAI),
                },
                {
                    headers,
                },
            );

            resolve(response);
        } catch (error) {
            reject(error);
        }
    });
}
/**
 * Helper method to read a file as text
 * @param {File} file - The file to read
 * @returns {Promise<string>} - Promise resolving to the file content as text
 */
readFileAsText(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            resolve(event.target.result);
        };
        reader.onerror = (error) => {
            reject(error);
        };
        reader.readAsText(file, 'utf-8');
    });
}

async duplicate(id) {
    const apiBaseUrl = process.env.REACT_APP_API_URL;
    const endpoint = `${apiBaseUrl}/api/v3/videos/${id}/duplicate`;
    const headers = getAuthHeaders(true);

    try {
        const response = await axios.post(
            endpoint,
            {},
            {
                headers,
            },
        );
        return response.data;
    } catch (error) {
        console.error('Error duplicating video:', error);
        throw error;
    }
}

/**
 * Transforms dubbing audio using various sources
 * @param {FormData} formData - Form data containing all required parameters
 * @returns {Promise} Promise resolving to the API response
 */
async transformDubbingAudio(formData) {
    const headers = getAuthHeaders(true);
    // For file uploads, don't set Content-Type, let the browser set it with the boundary
    delete headers['Content-Type'];

    try {
        const apiBaseUrl = process.env.REACT_APP_API_URL;
        const endpoint = `${apiBaseUrl}/api/v3/videos/dubbing/transform`;

        const response = await axios.post(endpoint, formData, {
            headers,
        });

        return response;
    } catch (error) {
        console.error('Error transforming dubbing audio:', error);
        throw error;
    }
}

/**
 * Export video with burned-in subtitles
 */
async exportVideoWithSubtitles(exportConfig) {
    const headers = getAuthHeaders(true);
    const endpoint = `${process.env.REACT_APP_API_URL}/api/v3/videos/export`;

    try {
        const response = await axios.post(endpoint, exportConfig, { headers });
        return response;
    } catch (error) {
        console.error('Error exporting video with subtitles:', error);
        throw error;
    }
}

/**
 * Get all exported videos for a specific video
 * 
 * @param {string} videoId - The ID of the video
 * @returns {Promise} - Promise resolving to list of exports
 */
async getVideoExports(videoId) {
    const headers = getAuthHeaders(true);
    const endpoint = `${process.env.REACT_APP_API_URL}/api/v3/videos/${videoId}/exports`;

    try {
        const response = await axios.get(endpoint, { headers });
        return response.data;
    } catch (error) {
        console.error('Error fetching video exports:', error);
        throw error;
    }
}

// ─── DubSync ─────────────────────────────────────────────────────────────────

/**
 * Step 1 – Transcribe dubbed audio and create a new "{language}-DubSync" entry.
 * @param {File|null} audioFile – Optional MP3 upload when no dubbed audio exists
 */
async dubSyncTranscribe(videoId, languageName, audioFile = null, noCheapMode = false, withSrtHint = false) {
    const headers = getAuthHeaders(true);
    try {
        const formData = new FormData();
        formData.append('videoId', videoId);
        formData.append('languageName', languageName);
        if (audioFile) {
            formData.append('audioFile', audioFile);
        }
        if (noCheapMode) {
            formData.append('noCheapMode', 'true');
        }
        if (withSrtHint) {
            formData.append('withSrtHint', 'true');
        }
        // Remove Content-Type so the browser sets it with the multipart boundary
        delete headers['Content-Type'];
        return await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v3/dubsync/transcribe`,
            formData,
            { headers },
        );
    } catch (error) {
        console.error('DubSync transcribe error:', error);
        throw error;
    }
}

/**
 * Step 3 – Align timestamps for the DubSync language.
 */
async dubSyncAlign(videoId, languageName) {
    const headers = getAuthHeaders(true);
    try {
        return await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v3/dubsync/align`,
            null,
            { headers, params: { videoId, languageName } },
        );
    } catch (error) {
        console.error('DubSync align error:', error);
        throw error;
    }
}

/**
 * Step 4 – Save manually edited timeline VTT.
 */
async dubSyncSaveTimeline(videoId, languageName, vttContent) {
    const headers = getAuthHeaders(true);
    try {
        return await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v3/dubsync/save-timeline`,
            { vttContent },
            { headers, params: { videoId, languageName } },
        );
    } catch (error) {
        console.error('DubSync save-timeline error:', error);
        throw error;
    }
}

/**
 * Step 5 – Render audio: stretch/compress/pad dubbed audio segments to match final timeline timings.
 * @param {string} vttContent – Live VTT built from the user's current (possibly unsaved) timeline edits.
 */
async dubSyncRenderAudio(videoId, languageName, vttContent) {
    const headers = getAuthHeaders(true);
    try {
        return await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v3/dubsync/render-audio`,
            { vttContent },
            { headers, params: { videoId, languageName } },
        );
    } catch (error) {
        console.error('DubSync render-audio error:', error);
        throw error;
    }
}

async dubSyncBootstrapManual(videoId, languageName) {
    const headers = getAuthHeaders(true);
    try {
        return await axios.post(
            `${process.env.REACT_APP_API_URL}/api/v3/dubsync/bootstrap-manual`,
            null,
            { headers, params: { videoId, languageName } },
        );
    } catch (error) {
        console.error('DubSync bootstrap-manual error:', error);
        throw error;
    }
}
}

const videoCRUDServiceInstance = new VideoCRUDService();
export default videoCRUDServiceInstance;
